/**
 * API Client for NestJS Backend Integration
 * Phase 3: Frontend-Backend Integration
 *
 * このファイルは、バックエンドAPIとの通信を担当するHTTPクライアントです。
 * - axios設定
 * - JWT認証ヘッダー
 * - エラーハンドリング（sonner toast連携）
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import logger from '../utils/logger';
import { loadEncryptedToken } from '../utils/crypto';
import { TOKEN_KEYS } from './auth/constants';
import {
  isSetupPendingError,
  handleUnauthorizedError,
} from './auth/shared/errorHandling';

const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
).trim();

const CUSTOMER_FETCH_FRIENDLY_ERROR =
  '顧客情報を取得できませんでした。時間をおいて再度お試しください。';

type FriendlyAxiosError = AxiosError & {
  _friendlyMessage?: string;
};

/**
 * APIクライアントクラス
 * シングルトンパターンで実装
 */
class ApiClient {
  private client: AxiosInstance;
  private static instance: ApiClient;

  /**
   * Unwraps `{ params: <value> }` single-key shorthand passed to
   * apiClient.get/request. Defense layer for callers that accidentally use
   * axios config-style `{ params }` wrapping.
   *
   * @reserved The single-key wrapper with key name `params` is always unwrapped.
   * Do NOT create backend endpoints with a query field literally named `params`,
   * or callers that legitimately need to send `{ params: <value> }` as the
   * actual query object. See Issue #3042 / PR #3478 for context.
   */
  private static normalizeQueryParams(params: unknown): unknown {
    if (
      params &&
      Object.prototype.toString.call(params) === '[object Object]' &&
      Object.keys(params as Record<string, unknown>).length === 1 &&
      'params' in (params as Record<string, unknown>)
    ) {
      const inner = (params as { params: unknown }).params;
      logger.warn(
        '[ApiClient] normalizeQueryParams unwrapped { params: ... } shorthand. ' +
          'Caller should pass flat object directly.',
        {
          innerKeys:
            inner &&
            Object.prototype.toString.call(inner) === '[object Object]'
              ? Object.keys(inner as Record<string, unknown>)
              : typeof inner,
        }
      );
      return inner;
    }

    return params;
  }

  private static normalizeRequestConfig<T extends AxiosRequestConfig>(
    config: T
  ): T {
    const normalizedParams = ApiClient.normalizeQueryParams(config.params);
    if (normalizedParams === config.params) {
      return config;
    }

    return {
      ...config,
      params: normalizedParams,
    };
  }

  private static getEndpoint(url: string | undefined): string | undefined {
    if (!url) return undefined;
    const withoutQuery = url.split('?')[0];
    try {
      return new URL(withoutQuery, API_BASE_URL).pathname;
    } catch {
      return withoutQuery;
    }
  }

  private static isCustomersFetch4xx(error: AxiosError): boolean {
    const statusCode = error.response?.status;
    const endpoint = ApiClient.getEndpoint(error.config?.url);
    const method = error.config?.method?.toUpperCase() ?? 'GET';

    const isCustomersEndpoint =
      endpoint === '/api/v1/customers' || endpoint === '/customers';

    return Boolean(
      method === 'GET' &&
        statusCode &&
        statusCode >= 400 &&
        statusCode < 500 &&
        statusCode !== 401 &&
        isCustomersEndpoint
    );
  }

  private static getSentryTenantId(): string | undefined {
    try {
      const raw = loadEncryptedToken(TOKEN_KEYS.TENANT_CONTEXT);
      if (!raw) return undefined;

      const parsed = JSON.parse(raw) as {
        tenantId?: unknown;
        id?: unknown;
      };
      const tenantId =
        typeof parsed.tenantId === 'string'
          ? parsed.tenantId
          : typeof parsed.id === 'string'
            ? parsed.id
            : undefined;

      return tenantId?.slice(0, 8);
    } catch {
      return undefined;
    }
  }

  private static wrapCustomersFetch4xx(error: AxiosError): void {
    if (!ApiClient.isCustomersFetch4xx(error)) return;

    const originalMessage = error.message;
    const endpoint = ApiClient.getEndpoint(error.config?.url);
    const statusCode = error.response?.status;
    (error as FriendlyAxiosError)._friendlyMessage =
      CUSTOMER_FETCH_FRIENDLY_ERROR;

    Sentry.captureMessage('customers-fetch 4xx response', {
      level: 'warning',
      extra: {
        tenantId: ApiClient.getSentryTenantId(),
        source: 'customers-fetch',
        endpoint,
        statusCode,
        originalMessage,
      },
    });
  }

  /**
   * Wait for Clerk SDK session to be ready
   * Issue #1579: Prevents 401 errors when SDK isn't initialized on page load
   */
  private async waitForClerkSession(
    maxWaitMs: number = 5000
  ): Promise<boolean> {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
      const clerk = (window as unknown as { Clerk?: { session?: unknown } })
        .Clerk;
      if (clerk?.session) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return false;
  }

  private static readonly MAX_SETUP_PENDING_RETRIES = 3;
  private static readonly SETUP_PENDING_BASE_DELAY_MS = 2000;

  /**
   * Issue #2229: setup-pending 401 のリトライ（exponential backoff）
   * 成功時は AxiosResponse を返却、非setup-pendingエラー時は AxiosError を返却、全リトライ失敗時は null
   */
  private async retrySetupPending(
    originalError: AxiosError
  ): Promise<AxiosResponse | AxiosError | null> {
    const config = originalError.config;
    if (!config) return null;

    for (
      let attempt = 0;
      attempt < ApiClient.MAX_SETUP_PENDING_RETRIES;
      attempt++
    ) {
      const delayMs =
        ApiClient.SETUP_PENDING_BASE_DELAY_MS * Math.pow(2, attempt);

      logger.info(
        `[ApiClient] Setup pending — retrying in ${delayMs}ms (${attempt + 1}/${ApiClient.MAX_SETUP_PENDING_RETRIES})`,
        { url: config.url }
      );

      toast.info(
        `アカウントを準備中です。しばらくお待ちください... (${attempt + 1}/${ApiClient.MAX_SETUP_PENDING_RETRIES})`,
        { id: 'setup-pending' }
      );

      await new Promise((resolve) => setTimeout(resolve, delayMs));

      try {
        // 防御的ヘッダークリア: request interceptor が fresh token で再設定する
        delete config.headers.Authorization;
        return await this.client(config);
      } catch (retryError) {
        const retryAxios = retryError as AxiosError;
        const retryData = retryAxios.response?.data as
          | { code?: string }
          | undefined;
        if (
          retryAxios.response?.status === 401 &&
          isSetupPendingError(retryData)
        ) {
          continue;
        }
        // 非setup-pendingエラー → エラーオブジェクトを返す（呼び出し元で処理）
        logger.warn('[ApiClient] Non-setup-pending error during retry', {
          status: retryAxios.response?.status,
        });
        return retryAxios;
      }
    }

    logger.warn('[ApiClient] Setup pending max retries exhausted', {
      url: config.url,
      attempts: ApiClient.MAX_SETUP_PENDING_RETRIES,
    });
    return null;
  }

  private constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      paramsSerializer: {
        indexes: null,
      },
    });

    // リクエストインターセプター（JWT認証 - Clerkトークン取得）
    // Issue #1579: Wait for Clerk SDK to be ready before making requests
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          // Wait for Clerk SDK to be ready (max 5 seconds)
          const isReady = await this.waitForClerkSession(5000);

          if (!isReady) {
            logger.warn(
              '[ApiClient] Clerk SDK not ready, request may fail:',
              config.url
            );
          }

          // Get token from Clerk SDK
          const clerk = (
            window as unknown as {
              Clerk?: { session?: { getToken: () => Promise<string | null> } };
            }
          ).Clerk;
          if (clerk?.session) {
            const token = await clerk.session.getToken();
            if (token && config.headers) {
              config.headers.Authorization = `Bearer ${token}`;
            } else {
              logger.warn('[ApiClient] No token available for:', config.url);
            }
          }
        } catch (error) {
          logger.error('[ApiClient] Failed to get Clerk token:', error);
          return Promise.reject(
            new Error(
              '認証トークンの取得に失敗しました。ページを再読み込みしてください。'
            )
          );
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // レスポンスインターセプター（データアンラップ + エラーハンドリング）
    // Issue #845: Backend TransformInterceptor wraps responses in { statusCode, message, data }
    this.client.interceptors.response.use(
      (response) => {
        // Check if response is wrapped in { statusCode, message, data } envelope
        if (
          response.data &&
          typeof response.data === 'object' &&
          'statusCode' in response.data &&
          'message' in response.data &&
          'data' in response.data
        ) {
          // Unwrap the data field to return just the actual data
          response.data = response.data.data;
        }
        return response;
      },
      async (error: AxiosError) => {
        // Issue #2229: setup-pending 401 はリトライ機構で処理
        const responseData = error.response?.data as
          | { code?: string }
          | undefined;
        if (
          error.response?.status === 401 &&
          isSetupPendingError(responseData)
        ) {
          const retried = await this.retrySetupPending(error);
          if (retried !== null && !axios.isAxiosError(retried)) return retried; // 成功
          if (axios.isAxiosError(retried)) {
            // 非setup-pendingエラー → 通常のエラーハンドリングに委譲
            this.handleError(retried as AxiosError);
            return Promise.reject(retried);
          }
          // retried === null → リトライ上限到達
          toast.error(
            'アカウントの準備に時間がかかっています。ページを再読み込みしてください。',
            { id: 'setup-pending' }
          );
          return Promise.reject(error);
        }

        // Issue #2318: 401 with active Clerk session → refresh token and retry once
        if (
          error.response?.status === 401 &&
          error.config &&
          !(error.config as unknown as { _clerkTokenRetried?: boolean })
            ._clerkTokenRetried
        ) {
          const clerk = (
            window as unknown as {
              Clerk?: {
                session?: {
                  getToken: (opts?: {
                    skipCache?: boolean;
                  }) => Promise<string | null>;
                };
              };
            }
          ).Clerk;
          if (clerk?.session) {
            try {
              const freshToken = await clerk.session.getToken({
                skipCache: true,
              });
              if (freshToken) {
                logger.info(
                  '[ApiClient] 401 with active Clerk session, retrying with fresh token',
                  { url: error.config.url }
                );
                error.config.headers.Authorization = `Bearer ${freshToken}`;
                (
                  error.config as unknown as { _clerkTokenRetried: boolean }
                )._clerkTokenRetried = true;
                return this.client(error.config);
              }
            } catch (tokenError) {
              logger.error('[ApiClient] Failed to refresh Clerk token', {
                tokenError,
                url: error.config?.url,
              });
            }
          }
        }

        const isSilent = Boolean(
          (error.config as unknown as Record<string, unknown>)?._silent
        );
        ApiClient.wrapCustomersFetch4xx(error);
        // _silent は toast を抑制するが、401 は再ログイン導線を失わないよう必ず処理する。
        if (error.response?.status === 401 || !isSilent) {
          this.handleError(error);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * シングルトンインスタンス取得
   */
  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * エラーハンドリング（sonner toast連携）
   */
  private handleError(error: AxiosError) {
    if (error.response) {
      const status = error.response.status;
      const friendlyMessage = (error as FriendlyAxiosError)._friendlyMessage;
      const message =
        friendlyMessage ||
        (error.response.data as { message?: string })?.message ||
        'エラーが発生しました';

      if (friendlyMessage || message === CUSTOMER_FETCH_FRIENDLY_ERROR) {
        toast.error(message);
        return;
      }

      switch (status) {
        case 400:
          toast.error(`入力エラー: ${message}`);
          break;
        case 401: {
          // Issue #1579: Enhanced 401 handling with debug info
          const responseData = error.response.data as {
            code?: string;
            message?: string;
          };

          logger.warn('[ApiClient] 401 Unauthorized', {
            url: error.config?.url,
            hasAuthHeader: !!error.config?.headers?.Authorization,
            errorCode: responseData?.code,
          });

          // Delegate to shared handler (setup-pending, Clerk session check, redirect)
          handleUnauthorizedError(responseData, 'ApiClient');
          break;
        }
        case 403:
          toast.error('アクセス権限がありません');
          break;
        case 404:
          toast.error('データが見つかりませんでした');
          break;
        case 409:
          toast.error(`重複エラー: ${message}`);
          break;
        case 500:
          toast.error('サーバーエラーが発生しました');
          break;
        default:
          toast.error(`エラー: ${message}`);
      }
    } else if (error.request) {
      toast.error('サーバーに接続できません');
    } else {
      toast.error('予期しないエラーが発生しました');
    }
  }

  /**
   * GET リクエスト
   * @param options.silent trueの場合、エラー時のtoast表示をスキップ
   */
  public get<T>(url: string, params?: unknown, options?: { silent?: boolean }) {
    return this.client.get<T>(url, {
      params: ApiClient.normalizeQueryParams(params),
      ...(options?.silent ? { _silent: true } : {}),
    } as import('axios').AxiosRequestConfig);
  }

  /**
   * POST リクエスト
   */
  public post<T>(url: string, data?: unknown, options?: { silent?: boolean }) {
    return this.client.post<T>(url, data, {
      ...(options?.silent ? { _silent: true } : {}),
    } as import('axios').AxiosRequestConfig);
  }

  /**
   * PUT リクエスト
   */
  public put<T>(url: string, data?: unknown) {
    return this.client.put<T>(url, data);
  }

  /**
   * PATCH リクエスト
   */
  public patch<T>(url: string, data?: unknown) {
    return this.client.patch<T>(url, data);
  }

  /**
   * DELETE リクエスト
   */
  public delete<T>(url: string) {
    return this.client.delete<T>(url);
  }

  /**
   * 汎用リクエスト（Orval custom instance用）
   * AxiosRequestConfig をそのまま渡し、interceptors を経由する
   */
  public request<T>(config: AxiosRequestConfig) {
    return this.client.request<T>(ApiClient.normalizeRequestConfig(config));
  }

  /**
   * 認証トークン設定
   */
  public setAuthToken(token: string) {
    localStorage.setItem('auth_token', token);
  }

  /**
   * 認証トークンクリア
   */
  public clearAuthToken() {
    localStorage.removeItem('auth_token');
  }
}

// シングルトンインスタンスをエクスポート
export const apiClient = ApiClient.getInstance();
export default apiClient;
